// DebugPanel - displays biome, miles, and object pool debug information

import { biomeRegistry } from '../registries/BiomeRegistry.js';

export class DebugPanel {
    constructor(config = {}) {
        // Dependencies (set via setDependencies)
        this.worldTranslator = null;
        this.biomeManager = null;
        this.gameState = null;
        this.poolManager = null;

        // State
        this.panel = null;
        this.enabled = config.enabled || false;

        // DOM element references for efficient updates
        this._elements = {};
    }

    /**
     * Set dependencies after construction
     */
    setDependencies(deps) {
        this.worldTranslator = deps.worldTranslator || this.worldTranslator;
        this.biomeManager = deps.biomeManager || this.biomeManager;
        this.gameState = deps.gameState || this.gameState;
        this.poolManager = deps.poolManager || this.poolManager;
    }

    /**
     * Create the debug panel DOM element
     */
    create() {
        this.panel = document.createElement('div');
        this.panel.id = 'debug-panel';
        this.panel.style.display = this.enabled ? 'block' : 'none';

        // Helper to create a row
        const createRow = (label, valueId, defaultValue) => {
            const row = document.createElement('div');
            row.className = 'debug-row';
            const labelSpan = document.createElement('span');
            labelSpan.className = 'debug-label';
            labelSpan.textContent = label;
            row.appendChild(labelSpan);
            const valueSpan = document.createElement('span');
            valueSpan.className = 'debug-value';
            valueSpan.id = valueId;
            valueSpan.textContent = defaultValue;
            row.appendChild(valueSpan);
            this._elements[valueId] = valueSpan;
            return row;
        };

        const createDivider = () => {
            const div = document.createElement('div');
            div.className = 'debug-divider';
            return div;
        };

        const createSectionHeader = (text) => {
            const header = document.createElement('div');
            header.className = 'debug-section-header';
            header.textContent = text;
            return header;
        };

        // Title
        const title = document.createElement('div');
        title.className = 'debug-title';
        title.textContent = 'WORLD DEBUG';
        this.panel.appendChild(title);

        // Biome section
        this.panel.appendChild(createSectionHeader('BIOME'));
        this.panel.appendChild(createRow('Current:', 'debug-biome-name', '-'));
        this.panel.appendChild(createRow('Miles:', 'debug-current-miles', '0'));
        this.panel.appendChild(createRow('Next Biome:', 'debug-next-biome', '-'));
        this.panel.appendChild(createRow('At Miles:', 'debug-next-biome-miles', '-'));

        this.panel.appendChild(createDivider());

        // Object Pool section
        this.panel.appendChild(createSectionHeader('OBJECT POOLS'));
        this.panel.appendChild(createRow('Total Active:', 'debug-pool-active', '0'));
        this.panel.appendChild(createRow('Total Free:', 'debug-pool-free', '0'));
        this.panel.appendChild(createRow('Total Created:', 'debug-pool-created', '0'));

        this.panel.appendChild(createDivider());

        // Pool breakdown container (dynamically populated)
        this.panel.appendChild(createSectionHeader('POOL BREAKDOWN'));
        const poolBreakdown = document.createElement('div');
        poolBreakdown.id = 'debug-pool-breakdown';
        poolBreakdown.className = 'debug-pool-breakdown';
        this.panel.appendChild(poolBreakdown);
        this._elements['pool-breakdown'] = poolBreakdown;

        this.panel.appendChild(createDivider());

        // World stats section
        this.panel.appendChild(createSectionHeader('WORLD'));
        this.panel.appendChild(createRow('World Z:', 'debug-world-z', '0'));
        this.panel.appendChild(createRow('Road Chunks:', 'debug-road-chunks', '0'));
        this.panel.appendChild(createRow('World Children:', 'debug-child-count', '0'));

        document.body.appendChild(this.panel);
    }

    /**
     * Update the debug panel with current stats
     */
    update() {
        if (!this.panel || !this.enabled) return;

        // Update biome info
        this._updateBiomeInfo();

        // Update pool stats
        this._updatePoolStats();

        // Update world stats
        this._updateWorldStats();
    }

    /**
     * Update biome information
     * @private
     */
    _updateBiomeInfo() {
        const currentMiles = this.gameState?.getTotalMiles?.() ?? 0;

        // Current biome
        const currentBiomeId = this.biomeManager?.currentBiomeId;
        const currentBiome = currentBiomeId ? biomeRegistry.get(currentBiomeId) : null;
        this._elements['debug-biome-name'].textContent = currentBiome?.name || '-';

        // Current miles
        this._elements['debug-current-miles'].textContent = Math.floor(currentMiles).toLocaleString();

        // Next biome calculation
        const sorted = biomeRegistry.getAllSorted();
        if (sorted.length > 0 && currentBiome) {
            // Find next biome in sequence
            const currentIndex = sorted.findIndex(b => b.id === currentBiomeId);
            const nextIndex = (currentIndex + 1) % sorted.length;
            const nextBiome = sorted[nextIndex];

            // Calculate miles to next biome
            // If we're looping, add the cycle length
            const cycleLength = sorted[sorted.length - 1].maxMiles - sorted[0].minMiles;
            let nextBiomeMiles = nextBiome.minMiles;

            // If next biome is before current in the cycle (we're looping)
            if (nextIndex <= currentIndex && sorted.length > 1) {
                // We need to complete current cycle first
                const currentCycle = Math.floor((currentMiles - sorted[0].minMiles) / cycleLength);
                nextBiomeMiles = sorted[0].minMiles + (currentCycle + 1) * cycleLength + (nextBiome.minMiles - sorted[0].minMiles);
            }

            this._elements['debug-next-biome'].textContent = nextBiome.name;
            this._elements['debug-next-biome-miles'].textContent = Math.floor(nextBiomeMiles).toLocaleString();
        } else {
            this._elements['debug-next-biome'].textContent = '-';
            this._elements['debug-next-biome-miles'].textContent = '-';
        }
    }

    /**
     * Update pool statistics
     * @private
     */
    _updatePoolStats() {
        const poolStats = this.poolManager?.getStats?.() ?? {};

        let totalActive = 0;
        let totalFree = 0;
        let totalCreated = 0;

        // Calculate totals and build breakdown
        const breakdownLines = [];
        for (const [typeId, stats] of Object.entries(poolStats)) {
            totalActive += stats.active || 0;
            totalFree += stats.free || 0;
            totalCreated += stats.total || 0;

            // Only show pools with activity
            if (stats.active > 0 || stats.total > 0) {
                breakdownLines.push(`${typeId}: ${stats.active}/${stats.total}`);
            }
        }

        this._elements['debug-pool-active'].textContent = totalActive.toString();
        this._elements['debug-pool-free'].textContent = totalFree.toString();
        this._elements['debug-pool-created'].textContent = totalCreated.toString();

        // Update breakdown (limit to top entries to avoid overflow)
        const breakdown = this._elements['pool-breakdown'];
        if (breakdown) {
            // Sort by active count descending and take top 8
            const sortedLines = breakdownLines
                .map(line => {
                    const match = line.match(/^(.+): (\d+)\/(\d+)$/);
                    return match ? { line, active: parseInt(match[2]) } : { line, active: 0 };
                })
                .sort((a, b) => b.active - a.active)
                .slice(0, 8)
                .map(item => item.line);

            breakdown.textContent = sortedLines.length > 0 ? sortedLines.join('\n') : 'No active pools';
        }
    }

    /**
     * Update world statistics
     * @private
     */
    _updateWorldStats() {
        const worldStats = this.worldTranslator?.getStats?.() ?? {};
        const biomeStats = this.biomeManager?.currentBiome?.getDebugStats?.() ?? {};

        this._elements['debug-world-z'].textContent = Math.round(worldStats.totalDistance || 0).toString();
        this._elements['debug-road-chunks'].textContent = (biomeStats.roadChunkCount || 0).toString();
        this._elements['debug-child-count'].textContent = (worldStats.childCount || 0).toString();
    }

    /**
     * Enable/disable the debug panel
     */
    setEnabled(enabled) {
        this.enabled = enabled;
        if (this.panel) {
            this.panel.style.display = enabled ? 'block' : 'none';
        }
    }

    /**
     * Check if enabled
     */
    isEnabled() {
        return this.enabled;
    }
}
